import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/joy";
import {
  CheckCircle,
  Clock,
  EnvelopeSimple,
  FloppyDisk,
} from "@phosphor-icons/react";
import type { PublicCampaignInfo } from "../../../types/hr";
import {
  publicAssessment,
  errorText,
  type AssessmentSession,
  type Answer,
  type AssessmentSettings,
} from "./assessmentApi";
import { AssessmentQuestionField } from "./AssessmentQuestionField";

export interface PublicAssessmentInfo {
  title: string;
  welcome: string;
  estimated_minutes: number;
  settings: AssessmentSettings;
}
export function CandidateAssessment({
  slug,
  campaign,
  assessment,
  renderApplication,
}: {
  slug: string;
  campaign: PublicCampaignInfo;
  assessment: PublicAssessmentInfo;
  renderApplication: (
    token: string,
    identity: { first_name: string; last_name: string; email: string },
    onComplete: () => void,
  ) => ReactNode;
}) {
  const [token, setToken] = useState(
    () =>
      new URLSearchParams(window.location.hash.slice(1)).get("assessment") ||
      sessionStorage.getItem(`assessment:${slug}`) ||
      "",
  );
  const [session, setSession] = useState<AssessmentSession | null>(null);
  const sessionRef = useRef<AssessmentSession | null>(null);
  const [identity, setIdentity] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [index, setIndex] = useState(0);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!token);
  const [saved, setSaved] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [resume, setResume] = useState(false);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  function accept(next: AssessmentSession) {
    sessionRef.current = next;
    setSession(next);
  }
  async function load() {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const next = await publicAssessment<AssessmentSession>(slug, "session", {
        token,
      });
      accept(next);
      setAnswers(next.answers);
      setSaved(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (token) {
      sessionStorage.setItem(`assessment:${slug}`, token);
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}#assessment=${encodeURIComponent(token)}`,
      );
      void load();
    }
  }, [token]); // token is the session boundary
  function save(operation = "save"): Promise<void> {
    const request = async () => {
      const current = sessionRef.current;
      if (!current?.applied) return;
      setBusy(true);
      setError("");
      const snapshot = structuredClone(answersRef.current);
      try {
        const next = await publicAssessment<AssessmentSession>(
          slug,
          "session",
          { token, operation, revision: current.revision, answers: snapshot },
        );
        accept(next);
        setSaved(
          JSON.stringify(snapshot) === JSON.stringify(answersRef.current),
        );
      } catch (e) {
        setError(errorText(e));
        throw e;
      } finally {
        setBusy(false);
      }
    };
    const result = queue.current.catch(() => undefined).then(request);
    queue.current = result;
    return result;
  }
  useEffect(() => {
    if (
      saved ||
      !session?.applied ||
      ["submitted", "reviewed", "withdrawn"].includes(session.status)
    )
      return;
    const timer = window.setTimeout(() => {
      void save().catch(() => undefined);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [answers]);
  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (!saved) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [saved]);
  async function register() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const next = await publicAssessment<AssessmentSession>(slug, "register", {
        ...identity,
        resume,
      });
      if (next.email_sent)
        setNotice(next.detail || "Check your email for your resume link.");
      else if (next.token) {
        accept(next);
        setToken(next.token);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function emailLink() {
    setBusy(true);
    setError("");
    try {
      await queue.current.catch(() => undefined);
      const result = await publicAssessment<{ delivery_accepted: boolean }>(
        slug,
        "session",
        { token, operation: "email" },
      );
      setNotice(
        result.delivery_accepted
          ? "Your private resume link has been requested. Check your email."
          : "Delivery could not be confirmed. Keep this page bookmarked and contact the hiring team if needed.",
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const questions =
    session?.definition.sections.flatMap((s) =>
      s.questions.map((q) => ({ ...q, section: s.title })),
    ) ?? [];
  const answered = questions.filter(
    (q) =>
      answers[q.id] !== undefined &&
      answers[q.id] !== "" &&
      !(Array.isArray(answers[q.id]) && !(answers[q.id] as string[]).length),
  ).length;
  const missing = questions.filter(
    (q) =>
      q.required &&
      (answers[q.id] === undefined || String(answers[q.id]).trim() === ""),
  );
  const current = questions[index];
  if (session?.applied === false && !loading)
    return (
      <>
        {renderApplication(token, session, () => {
          void load();
        })}
      </>
    );
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.body",
        px: 2,
        py: { xs: 3, sm: 5 },
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: 760, mx: "auto" }}>
        <Box sx={{ textAlign: "center" }}>
          {campaign.organization_logo && (
            <Box
              component="img"
              src={campaign.organization_logo}
              alt={campaign.organization_name}
              sx={{ maxWidth: 220, maxHeight: 64, mb: 2 }}
            />
          )}
          <Typography level="body-lg">{campaign.organization_name}</Typography>
          <Typography level="h2">{campaign.role_title}</Typography>
          <Typography>{campaign.location}</Typography>
        </Box>
        {error && (
          <Alert color="danger" sx={{ overflowWrap: "anywhere" }}>
            {error}
            <Button variant="plain" onClick={load}>
              Reload saved session
            </Button>
          </Alert>
        )}
        {notice && <Alert color="primary">{notice}</Alert>}
        {loading ? (
          <Card>
            <Typography>Opening your saved assessment…</Typography>
            <LinearProgress />
          </Card>
        ) : token && !session ? (
          <Card>
            <Typography>
              Use your latest resume link, or request one below.
            </Typography>
            <Button
              onClick={() => {
                setToken("");
                sessionStorage.removeItem(`assessment:${slug}`);
                window.history.replaceState(null, "", window.location.pathname);
                setResume(true);
              }}
            >
              Request a resume link
            </Button>
          </Card>
        ) : !session ? (
          <>
            <Card sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography level="h3">
                {resume ? "Welcome back" : "Show us how you work"}
              </Typography>
              <Typography>{assessment.welcome}</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Clock />
                <Typography>
                  About {assessment.estimated_minutes} minutes · Save and return
                  anytime
                </Typography>
              </Stack>
              <Typography level="body-sm">
                No camera, fullscreen mode, or account required. A calculator is
                welcome. Answer in your own words. Please contact the hiring
                team if you need another way to complete the assessment.
              </Typography>
            </Card>
            <Box component="details">
              <Box component="summary" sx={{ cursor: "pointer" }}>
                Role details and hiring team contact
              </Box>
              <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
                {campaign.description}\n{assessment.settings.role_details}
              </Typography>
              <Typography sx={{ mt: 1 }}>
                {assessment.settings.help_contact}
              </Typography>
            </Box>
            <Card
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                void register();
              }}
            >
              <Typography level="title-lg">
                {resume
                  ? "Send a private resume link"
                  : "Save your details and get started"}
              </Typography>
              {!resume && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  {(["first_name", "last_name"] as const).map((key) => (
                    <FormControl required key={key} sx={{ flex: 1 }}>
                      <FormLabel>
                        {key === "first_name" ? "First name" : "Last name"}
                      </FormLabel>
                      <Input
                        autoComplete={
                          key === "first_name" ? "given-name" : "family-name"
                        }
                        value={identity[key]}
                        onChange={(e) =>
                          setIdentity({ ...identity, [key]: e.target.value })
                        }
                      />
                    </FormControl>
                  ))}
                </Stack>
              )}
              <FormControl required>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  autoComplete="email"
                  value={identity.email}
                  onChange={(e) =>
                    setIdentity({ ...identity, email: e.target.value })
                  }
                />
              </FormControl>
              <Typography level="body-sm">
                By continuing, you agree to save your name and email with{" "}
                {campaign.organization_name} for this application. Their hiring
                team may follow up if you do not finish.
              </Typography>
              <Button type="submit" size="lg" loading={busy}>
                {resume ? "Email my resume link" : "Save & continue"}
              </Button>
              <Button
                variant="plain"
                onClick={() => {
                  setResume(!resume);
                  setNotice("");
                }}
              >
                {resume
                  ? "Start a new application"
                  : "Already started? Resume your application"}
              </Button>
            </Card>
          </>
        ) : ["submitted", "reviewed", "withdrawn"].includes(session.status) ? (
          <Card sx={{ textAlign: "center", p: 4 }}>
            <CheckCircle size={48} style={{ alignSelf: "center" }} />
            <Typography level="h3">
              {session.status === "withdrawn"
                ? "Assessment withdrawn"
                : "You’re all set"}
            </Typography>
            <Typography>
              {session.status === "withdrawn"
                ? "Your hiring team can help if you want to continue later."
                : "Your answers are saved. The hiring team will review your application and follow up about next steps. You do not need to keep this page open."}
            </Typography>
            <Typography>{session.settings.help_contact}</Typography>
          </Card>
        ) : (
          <>
            <Card>
              <Stack
                direction="row"
                sx={{ justifyContent: "space-between", gap: 1 }}
              >
                <Typography level="title-md">
                  {review ? "Review your answers" : current?.section}
                </Typography>
                <Typography level="body-sm">
                  {answered} / {questions.length} answered
                </Typography>
              </Stack>
              <LinearProgress
                determinate
                value={
                  questions.length ? (answered / questions.length) * 100 : 0
                }
              />
              <Typography level="body-sm" aria-live="polite">
                {busy
                  ? "Saving…"
                  : saved
                    ? "All changes saved"
                    : "Unsaved changes"}
              </Typography>
            </Card>
            {review ? (
              <>
                <Typography level="h3">Ready to submit?</Typography>
                <Typography>
                  Review your responses below. You can edit them until you
                  submit.
                </Typography>
                {questions.map((q, i) => (
                  <Card key={q.id}>
                    <Typography level="title-md">
                      {i + 1}. {q.prompt}
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap" }}>
                      {q.options?.length
                        ? q.options
                            .filter((o) =>
                              Array.isArray(answers[q.id])
                                ? (answers[q.id] as string[]).includes(o.id)
                                : o.id === answers[q.id],
                            )
                            .map((o) => o.label)
                            .join(", ") || "Not answered"
                        : String(answers[q.id] ?? "Not answered")}
                    </Typography>
                    <Button
                      variant="plain"
                      onClick={() => {
                        setIndex(i);
                        setReview(false);
                        window.scrollTo({ top: 0 });
                      }}
                    >
                      Edit answer
                    </Button>
                  </Card>
                ))}
                {missing.length > 0 && (
                  <Alert color="warning">
                    {missing.length} required answer(s) still need attention.
                  </Alert>
                )}
                <Button
                  size="lg"
                  loading={busy}
                  disabled={missing.length > 0}
                  onClick={() => {
                    void save("submit").catch(() => undefined);
                  }}
                >
                  Submit assessment
                </Button>
              </>
            ) : (
              current && (
                <Card sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography level="body-sm">
                    Question {index + 1} of {questions.length}
                    {!current.required ? " · Optional" : ""}
                  </Typography>
                  <AssessmentQuestionField
                    question={current}
                    value={answers[current.id]}
                    onChange={(value) => {
                      setAnswers((prev) => ({ ...prev, [current.id]: value }));
                      setSaved(false);
                    }}
                  />
                  <Stack
                    direction="row"
                    sx={{ justifyContent: "space-between", gap: 1, mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      disabled={busy || index === 0}
                      onClick={() => {
                        void save()
                          .then(() => setIndex(index - 1))
                          .catch(() => undefined);
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      loading={busy}
                      onClick={() => {
                        void save()
                          .then(() => {
                            if (index === questions.length - 1) setReview(true);
                            else setIndex(index + 1);
                            window.scrollTo({ top: 0 });
                          })
                          .catch(() => undefined);
                      }}
                    >
                      {index === questions.length - 1
                        ? "Review answers"
                        : "Save & next"}
                    </Button>
                  </Stack>
                </Card>
              )
            )}
            <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
              <Button
                startDecorator={<FloppyDisk />}
                variant="outlined"
                disabled={busy}
                onClick={() => {
                  void save()
                    .then(() =>
                      setNotice(
                        "Saved. You can close this page and return using your private link.",
                      ),
                    )
                    .catch(() => undefined);
                }}
              >
                Save for later
              </Button>
              <Button
                startDecorator={<EnvelopeSimple />}
                variant="plain"
                disabled={busy}
                onClick={emailLink}
              >
                Email my resume link
              </Button>
              <Button variant="plain" onClick={() => setReview(!review)}>
                {review ? "Back to questions" : "Review all answers"}
              </Button>
            </Stack>
            <Box component="details">
              <Box component="summary" sx={{ cursor: "pointer" }}>
                Role details & help
              </Box>
              <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
                {session.settings.role_details}
              </Typography>
              <Typography>{session.settings.help_contact}</Typography>
              <Button
                color="danger"
                variant="plain"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      "Withdraw this assessment? Your saved application remains available to the hiring team, but you will not receive further assessment reminders.",
                    )
                  )
                    void save("withdraw").catch(() => undefined);
                }}
              >
                Withdraw assessment
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}
